import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Icon, IconButton, Input, Segmented, Select, TextArea, useLayout, useToast, type Tone } from "@agentsaas/ui";
import type { Actor, Agent, AgentId, Meeting, Message, UserId } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatDateTime, formatMicroUsd, formatTime } from "../app/format";
import { useAgents } from "../data/queries";
import { ck } from "../data/collab";
import { putMeeting, useMeeting, useMeetings, useMeetingStream } from "../data/meetings";
import { AgentStatusDot } from "../agent/TeamList";
import { ProjectChrome, ProjectNav } from "../project/ProjectChrome";
import { MobileFrame } from "../mobile/MobileProject";
import { Loading } from "../layout/Loading";
import { textOfParts } from "../rooms/model";
import {
  availableActions,
  clearDraft,
  loadDraft,
  minutesMarkdown,
  normalizeMinutes,
  parseAgenda,
  roundProgress,
  saveDraft,
  usdToMicro,
  type MeetingAction,
  type Minutes,
} from "./model";

/**
 * /p/:pid/meetings[/:mid]：会议。
 * - 列表 + 发起会议（议程、参与者、主持人、轮数、预算）
 * - 会议室：当前发言者高亮、轮次进度、人插话、推进下一轮、结束会议；结束后看纪要（可在本机编辑、行动项建任务 / 跳转任务）
 * - 手机：放在"对话" tab（会话中心里有会议入口），会议室全屏下钻，可旁听和插话
 */

export const MEETING_TONE: Record<Meeting["state"], Tone> = { created: "neutral", running: "accent", summarizing: "warning", closed: "success" };

export function MeetingStateBadge({ m }: { m: Pick<Meeting, "state"> }) {
  const { t } = useT();
  return (
    <Badge tone={MEETING_TONE[m.state]}>
      <span data-testid="meeting-state" data-state={m.state}>
        {t(`meeting.state.${m.state}`)}
      </span>
    </Badge>
  );
}

function MeetingsFrame({ pid, children }: { pid: string; children: React.ReactNode }) {
  const layout = useLayout();
  return (
    <>
      <ProjectChrome pid={pid} />
      {layout === "mobile" ? (
        <MobileFrame pid={pid} active="chat">
          {children}
        </MobileFrame>
      ) : (
        <div className="pframe">
          <ProjectNav pid={pid} active="meetings" />
          <div className="pframe__body pframe__body--scroll">{children}</div>
        </div>
      )}
    </>
  );
}

export function MeetingsPage() {
  const { pid = "" } = useParams();
  return (
    <MeetingsFrame pid={pid}>
      <div className="page">
        <MeetingList pid={pid} />
      </div>
    </MeetingsFrame>
  );
}

/** 会议列表（也嵌在手机的会话中心里） */
export function MeetingList({ pid, compact }: { pid: string; compact?: boolean }) {
  const { t, locale } = useT();
  const tz = useSession().state.user?.timezone;
  const meetings = useMeetings(pid);
  const [open, setOpen] = useState(false);
  const Title = compact ? "h2" : "h1";
  return (
    <section className="stack" aria-labelledby="meetings-title">
      <div className={compact ? "row" : "page__head"}>
        <Title id="meetings-title" className={compact ? "section-title grow" : "page__title"}>
          {t("meeting.title")}
        </Title>
        <Button variant={compact ? "secondary" : "primary"} size={compact ? "sm" : "md"} icon="plus" onClick={() => setOpen(true)} data-testid="meeting-new">
          {t("meeting.new")}
        </Button>
      </div>
      {meetings.isPending ? (
        <Loading />
      ) : meetings.data?.length ? (
        <ul className="list card meeting-list" style={{ padding: 0 }} data-testid="meeting-list">
          {meetings.data.map((m) => (
            <li key={m.id}>
              <Link to={`/p/${pid}/meetings/${m.id}`} className="list-item" data-testid="meeting-item">
                <Icon name="mic" />
                <span className="grow">
                  <strong className="ellipsis">{m.title}</strong>
                  <span className="small muted meeting-item__meta">
                    {t("meeting.round_of", { round: m.round, max: m.maxRounds })}
                    <span className="sep" aria-hidden="true" />
                    {t("meeting.participants_count", { count: m.participants.length })}
                    <span className="sep" aria-hidden="true" />
                    <time dateTime={m.createdAt}>{formatDateTime(m.createdAt, locale, tz, { dateStyle: "medium", timeStyle: "short" })}</time>
                  </span>
                </span>
                <MeetingStateBadge m={m} />
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="mic" title={t("meeting.empty")} />
      )}
      <NewMeetingDialog pid={pid} open={open} onClose={() => setOpen(false)} />
    </section>
  );
}

// ───────────── 发起会议 ─────────────

export function NewMeetingDialog({ pid, open, onClose }: { pid: string; open: boolean; onClose(): void }) {
  const { t } = useT();
  const errText = useErrorText();
  const { api } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useSession().state.user;
  const agents = useAgents(pid).data ?? [];
  const [title, setTitle] = useState("");
  const [agenda, setAgenda] = useState("");
  const [picked, setPicked] = useState<AgentId[]>([]);
  const [moderator, setModerator] = useState("me");
  const [rounds, setRounds] = useState("3");
  const [budget, setBudget] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setAgenda("");
    setPicked([]);
    setModerator("me");
    setRounds("3");
    setBudget("");
    setError(null);
  }, [open]);

  const items = parseAgenda(agenda);
  const nRounds = Number(rounds);
  const budgetMicro = usdToMicro(budget);
  const roundsOk = Number.isInteger(nRounds) && nRounds >= 1 && nRounds <= 20;
  const valid = title.trim() && items.length > 0 && picked.length > 0 && roundsOk && budgetMicro !== undefined;
  const toggle = (aid: AgentId) => setPicked((xs) => (xs.includes(aid) ? xs.filter((x) => x !== aid) : [...xs, aid]));

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid || !me) return;
    setBusy(true);
    setError(null);
    const mod: Actor = moderator === "me" ? { type: "user", id: me.id as UserId } : { type: "agent", id: moderator as AgentId };
    try {
      const m = await api.call("meetingCreate", {
        params: { pid },
        body: { title: title.trim(), agenda: items, moderator: mod, participants: picked, maxRounds: nRounds, budgetMicroUsd: budgetMicro ?? null },
      });
      putMeeting(qc, m);
      onClose();
      navigate(`/p/${pid}/meetings/${m.id}`);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("meeting.new")}
      closeLabel={t("shell.close")}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" loading={busy} disabled={!valid} onClick={() => void submit()} data-testid="meeting-submit">
            {t("meeting.create")}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} className="meeting-form">
        {error ? (
          <p className="form-error" role="alert">
            {error}
          </p>
        ) : null}
        <Field label={t("meeting.field.title")}>
          {(id) => <Input id={id} data-autofocus value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="meeting-title" />}
        </Field>
        <Field label={t("meeting.field.agenda")} hint={t("meeting.field.agenda_hint")}>
          {(id, d) => <TextArea id={id} aria-describedby={d} rows={4} value={agenda} onChange={(e) => setAgenda(e.target.value)} data-testid="meeting-agenda" />}
        </Field>
        <fieldset className="meeting-form__agents">
          <legend className="ui-field__label">{t("meeting.field.participants")}</legend>
          {agents.length ? (
            agents.map((a) => (
              <label key={a.id} className="check-row">
                <input type="checkbox" checked={picked.includes(a.id)} onChange={() => toggle(a.id)} data-testid={`meeting-agent-${a.name}`} />
                <AgentStatusDot status={a.status} />
                <span className="grow">{a.name}</span>
                <span className="muted small">{a.role}</span>
              </label>
            ))
          ) : (
            <p className="muted small">{t("meeting.no_agents")}</p>
          )}
        </fieldset>
        <div className="form-grid">
          <Field label={t("meeting.field.moderator")}>
            {(id) => (
              <Select id={id} value={moderator} onChange={(e) => setModerator(e.target.value)} data-testid="meeting-moderator">
                <option value="me">{t("meeting.moderator_me")}</option>
                {agents
                  .filter((a) => picked.includes(a.id))
                  .map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
          <Field label={t("meeting.field.rounds")} error={roundsOk ? undefined : t("meeting.field.rounds_invalid")}>
            {(id) => <Input id={id} type="number" inputMode="numeric" min={1} max={20} value={rounds} onChange={(e) => setRounds(e.target.value)} data-testid="meeting-rounds" />}
          </Field>
          <Field label={t("meeting.field.budget")} hint={t("meeting.field.budget_hint")} error={budgetMicro === undefined ? t("meeting.field.budget_invalid") : undefined}>
            {(id, d) => <Input id={id} aria-describedby={d} inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="meeting-budget" />}
          </Field>
        </div>
      </form>
    </Dialog>
  );
}

// ───────────── 会议室 ─────────────

export function MeetingRoomPage() {
  const { pid = "", mid = "" } = useParams();
  return (
    <MeetingsFrame pid={pid}>
      <MeetingRoom key={mid} pid={pid} mid={mid} />
    </MeetingsFrame>
  );
}

function useNameOf(pid: string) {
  const { t } = useT();
  const me = useSession().state.user;
  const agents = useAgents(pid).data ?? [];
  return (a: Actor) => {
    if (a.type === "system") return t("room.system");
    if (a.type === "user") return me && a.id === me.id ? t("chat.you") : t("room.member_user");
    return agents.find((x) => x.id === a.id)?.name ?? a.id;
  };
}

function MeetingRoom({ pid, mid }: { pid: string; mid: string }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { api } = useServices();
  const layout = useLayout();
  const mobile = layout === "mobile";
  const me = useSession().state.user;
  const meeting = useMeeting(mid);
  const agents = useAgents(pid).data ?? [];
  const stream = useMeetingStream(meeting.data);
  const nameOf = useNameOf(pid);
  const [busy, setBusy] = useState<MeetingAction | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [view, setView] = useState<"minutes" | "transcript">("minutes");
  const logRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  useLayoutEffect(() => {
    const el = logRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [stream.messages.length, view]);

  if (meeting.isPending) return <Loading />;
  if (!meeting.data) return <EmptyState icon="mic" title={t("error.common.not_found")} />;
  const m = meeting.data;
  const can = availableActions(m);
  const ended = m.state === "closed" || m.state === "summarizing";
  const speaker = m.currentSpeaker ? agents.find((a) => a.id === m.currentSpeaker) : undefined;

  const act = async (action: MeetingAction) => {
    setBusy(action);
    try {
      putMeeting(qc, await api.call("meetingAction", { params: { mid, action } }));
      if (action === "close") {
        setConfirmClose(false);
        setView("minutes");
      }
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      void qc.invalidateQueries({ queryKey: ["meeting", mid] });
    } finally {
      setBusy(null);
    }
  };

  const interject = async (text: string) => {
    try {
      stream.add(await api.call("meetingInterject", { params: { mid }, body: { text } }));
      stick.current = true;
      return true;
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      return false;
    }
  };

  const progress = roundProgress(m);
  const head = (
    <div className={mobile ? "drill-head meeting-head" : "meeting-head"}>
      {mobile ? <IconButton icon="back" label={t("common.back")} onClick={() => navigate(`/p/${pid}/rooms`)} data-testid="drill-back" /> : null}
      <div className="grow meeting-head__main">
        <h1 className="meeting-head__title ellipsis" data-testid="meeting-room-title">
          {m.title}
        </h1>
        <div className="row small">
          <MeetingStateBadge m={m} />
          <span className="muted" data-testid="meeting-round">
            {t("meeting.round_of", { round: m.round, max: m.maxRounds })}
          </span>
        </div>
      </div>
    </div>
  );

  const actions = (
    <div className="row meeting-actions">
      {can.start ? (
        <Button variant="primary" icon="play" onClick={() => void act("start")} loading={busy === "start"} data-testid="meeting-start">
          {t("meeting.action.start")}
        </Button>
      ) : null}
      {m.state === "running" ? (
        <Button
          variant={can["next-round"] ? "primary" : "secondary"}
          icon="chevronRight"
          onClick={() => void act("next-round")}
          disabled={!can["next-round"]}
          loading={busy === "next-round"}
          data-testid="meeting-next"
        >
          {t("meeting.action.next_round")}
        </Button>
      ) : null}
      {can.close ? (
        <Button variant="danger" icon="stop" onClick={() => setConfirmClose(true)} data-testid="meeting-close">
          {t("meeting.action.close")}
        </Button>
      ) : null}
    </div>
  );

  const log = (
    <div className="room__log meeting-log" ref={logRef} onScroll={() => {
      const el = logRef.current;
      if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    }} role="log" aria-live="polite" aria-label={t("meeting.transcript")} data-testid="meeting-log">
      {!stream.loaded ? <div className="chat__status muted small">{t("chat.history_loading")}</div> : null}
      {stream.loaded && !stream.messages.length ? <div className="chat__status muted">{m.state === "created" ? t("meeting.not_started") : t("room.empty")}</div> : null}
      {stream.messages.map((msg) => (
        <MeetingMessage key={msg.id} msg={msg} name={nameOf(msg.from)} mine={!!me && msg.from.type === "user" && msg.from.id === me.id} time={formatTime(msg.createdAt, locale, me?.timezone)} />
      ))}
      {speaker ? (
        <div className="room__typing small muted" data-testid="meeting-speaking">
          <span className="typing-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          {t("meeting.speaking", { name: speaker.name })}
        </div>
      ) : null}
    </div>
  );

  const main = (
    <div className="meeting__main">
      {head}
      <div className="meeting__bar">
        <div className="meeting-progress" role="progressbar" aria-label={t("meeting.progress")} aria-valuemin={0} aria-valuemax={m.maxRounds} aria-valuenow={ended ? m.maxRounds : m.round} aria-valuetext={t("meeting.round_of", { round: m.round, max: m.maxRounds })}>
          <div className="meeting-progress__fill" style={{ width: `${Math.round(progress * 100)}%` }} />
        </div>
        {actions}
      </div>
      {mobile ? <ParticipantStrip m={m} agents={agents} /> : null}
      {ended ? (
        <div className="meeting__tabs">
          <Segmented<"minutes" | "transcript">
            label={t("meeting.view")}
            value={view}
            onChange={setView}
            options={[
              { value: "minutes", label: t("meeting.minutes"), icon: "file" },
              { value: "transcript", label: t("meeting.transcript"), icon: "chat" },
            ]}
          />
        </div>
      ) : null}
      {ended && view === "minutes" ? (
        <div className="meeting__minutes">
          {m.state === "summarizing" || !m.minutes ? (
            <div className="center-fill" role="status" data-testid="meeting-summarizing">
              {t("meeting.summarizing")}
            </div>
          ) : (
            <MinutesPanel pid={pid} m={m} minutes={m.minutes} agents={agents} />
          )}
        </div>
      ) : (
        log
      )}
      {ended ? null : <InterjectBox onSend={interject} />}
    </div>
  );

  return (
    <div className={`meeting${mobile ? " meeting--mobile" : ""}`} data-testid="meeting-room" data-state={m.state}>
      {main}
      {mobile ? null : (
        <aside className="meeting__aside" aria-label={t("meeting.details")}>
          <section className="stack">
            <h2 className="section-title">{t("meeting.agenda")}</h2>
            <ol className="meeting-agenda" data-testid="meeting-agenda-list">
              {m.agenda.map((a, i) => (
                <li key={i} className={m.state === "running" && (m.round - 1) % m.agenda.length === i ? "is-current" : undefined}>
                  {a}
                </li>
              ))}
            </ol>
          </section>
          <section className="stack">
            <h2 className="section-title">{t("meeting.participants")}</h2>
            <ParticipantList m={m} agents={agents} />
            <p className="small muted">{t("meeting.moderator", { name: nameOf(m.moderator) })}</p>
          </section>
          {m.budgetMicroUsd !== null ? <p className="small muted">{t("meeting.budget", { budget: formatMicroUsd(m.budgetMicroUsd, locale) })}</p> : null}
        </aside>
      )}
      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        title={t("meeting.close_title")}
        closeLabel={t("shell.close")}
        size="sm"
        footer={
          <>
            <Button onClick={() => setConfirmClose(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" loading={busy === "close"} onClick={() => void act("close")} data-testid="meeting-close-confirm">
              {t("meeting.action.close")}
            </Button>
          </>
        }
      >
        <p>{t("meeting.close_body")}</p>
      </Dialog>
    </div>
  );
}

function ParticipantList({ m, agents }: { m: Meeting; agents: Agent[] }) {
  const { t } = useT();
  return (
    <ul className="list meeting-people" data-testid="meeting-participants">
      {m.participants.map((aid) => {
        const a = agents.find((x) => x.id === aid);
        const speaking = m.currentSpeaker === aid;
        return (
          <li key={aid} className={`meeting-person${speaking ? " is-speaking" : ""}`} aria-current={speaking ? "true" : undefined} data-testid="meeting-participant" data-speaking={speaking ? "true" : "false"}>
            {a ? <AgentStatusDot status={a.status} /> : null}
            <span className="grow ellipsis">{a?.name ?? aid}</span>
            {speaking ? <Badge tone="accent">{t("meeting.speaking_badge")}</Badge> : null}
          </li>
        );
      })}
    </ul>
  );
}

/** 手机：参与者横向条，当前发言者高亮 */
function ParticipantStrip({ m, agents }: { m: Meeting; agents: Agent[] }) {
  const { t } = useT();
  return (
    <ul className="meeting-strip" aria-label={t("meeting.participants")} data-testid="meeting-participants">
      {m.participants.map((aid) => {
        const a = agents.find((x) => x.id === aid);
        const speaking = m.currentSpeaker === aid;
        return (
          <li key={aid} className={`chip meeting-chip${speaking ? " is-speaking" : ""}`} aria-current={speaking ? "true" : undefined} data-testid="meeting-participant" data-speaking={speaking ? "true" : "false"}>
            {speaking ? <Icon name="mic" size={14} aria-label={t("meeting.speaking_badge")} /> : null}
            {a?.name ?? aid}
          </li>
        );
      })}
    </ul>
  );
}

function MeetingMessage({ msg, name, mine, time }: { msg: Message; name: string; mine: boolean; time: string }) {
  const { t } = useT();
  return (
    <article className={`rmsg rmsg--${msg.from.type}${mine ? " rmsg--mine" : ""}`} data-testid="meeting-msg" data-from={name} aria-label={t("room.message_from", { name })}>
      <header className="rmsg__head small">
        {msg.from.type === "agent" ? <Icon name="user" size={14} /> : null}
        <strong>{name}</strong>
        <time className="muted" dateTime={msg.createdAt}>
          {time}
        </time>
      </header>
      <p className="rmsg__text">{textOfParts(msg.parts)}</p>
    </article>
  );
}

function InterjectBox({ onSend }: { onSend(text: string): Promise<boolean> }) {
  const { t } = useT();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const v = text.trim();
    if (!v || busy) return;
    setBusy(true);
    if (await onSend(v)) setText("");
    setBusy(false);
  };
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send();
    }
  };
  return (
    <form className="composer meeting-interject" onSubmit={send}>
      <TextArea
        className="composer__input"
        rows={1}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKey}
        placeholder={t("meeting.interject_placeholder")}
        aria-label={t("meeting.interject")}
        data-testid="meeting-interject"
      />
      <IconButton icon="send" label={t("meeting.interject_send")} type="submit" disabled={!text.trim() || busy} data-testid="meeting-interject-send" />
    </form>
  );
}

// ───────────── 纪要 ─────────────

function MinutesPanel({ pid, m, minutes, agents }: { pid: string; m: Meeting; minutes: Minutes; agents: Agent[] }) {
  const { t } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const qc = useQueryClient();
  const { api } = useServices();
  const [draft, setDraft] = useState<Minutes | null>(() => loadDraft(m.id));
  const [editing, setEditing] = useState<Minutes | null>(null);
  const [creating, setCreating] = useState<number | null>(null);
  const shown = draft ?? minutes;
  const nameOf = (aid: AgentId) => agents.find((a) => a.id === aid)?.name ?? aid;

  const persist = (next: Minutes) => {
    setDraft(next);
    saveDraft(m.id, next);
  };

  const createTask = async (i: number) => {
    const item = shown.actionItems[i];
    if (!item) return;
    setCreating(i);
    try {
      const task = await api.call("taskCreate", {
        params: { pid },
        body: { title: item.title, spec: t("meeting.task_spec", { title: m.title }), ...(item.assignee ? { assignee: item.assignee } : {}) },
      });
      void qc.invalidateQueries({ queryKey: ck.tasks(pid) });
      persist({ ...shown, actionItems: shown.actionItems.map((a, j) => (j === i ? { ...a, taskId: task.id } : a)) });
      toast.show({ message: t("meeting.task_created"), tone: "success", duration: 1500 });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      setCreating(null);
    }
  };

  const copy = async () => {
    const md = minutesMarkdown(m.title, shown, { summary: t("meeting.summary"), decisions: t("meeting.decisions"), actions: t("meeting.action_items") }, nameOf);
    try {
      await navigator.clipboard.writeText(md);
      toast.show({ message: t("meeting.copied"), tone: "success", duration: 1500 });
    } catch {
      toast.show({ message: t("meeting.copy_failed"), tone: "danger" });
    }
  };

  if (editing) return <MinutesEditor value={editing} agents={agents} onCancel={() => setEditing(null)} onSave={(v) => {
          persist(normalizeMinutes(v));
          setEditing(null);
        }} />;

  return (
    <div className="stack minutes" data-testid="meeting-minutes">
      <div className="row">
        <h2 className="section-title grow">{t("meeting.minutes")}</h2>
        <Button size="sm" icon="copy" onClick={() => void copy()} data-testid="minutes-copy">
          {t("meeting.copy")}
        </Button>
        <Button size="sm" icon="edit" onClick={() => setEditing(shown)} data-testid="minutes-edit">
          {t("meeting.edit_minutes")}
        </Button>
      </div>
      {draft ? (
        <div className="banner banner--warning" role="note" data-testid="minutes-local">
          <Icon name="save" size={16} />
          <span className="grow">{t("meeting.local_edits")}</span>
          <Button size="sm" variant="ghost" onClick={() => (clearDraft(m.id), setDraft(null))} data-testid="minutes-reset">
            {t("meeting.reset_minutes")}
          </Button>
        </div>
      ) : null}
      <section className="stack">
        <h3 className="minutes__h">{t("meeting.summary")}</h3>
        <p className="minutes__summary" data-testid="minutes-summary">
          {shown.summary}
        </p>
      </section>
      <section className="stack">
        <h3 className="minutes__h">{t("meeting.decisions")}</h3>
        {shown.decisions.length ? (
          <ul className="minutes__list" data-testid="minutes-decisions">
            {shown.decisions.map((d, i) => (
              <li key={i}>{d}</li>
            ))}
          </ul>
        ) : (
          <p className="muted small">{t("meeting.none")}</p>
        )}
      </section>
      <section className="stack">
        <h3 className="minutes__h">{t("meeting.action_items")}</h3>
        {shown.actionItems.length ? (
          <ul className="list card minutes__actions" style={{ padding: 0 }} data-testid="minutes-actions">
            {shown.actionItems.map((a, i) => (
              <li key={i} className="list-item" data-testid="minutes-action">
                <Icon name="check" />
                <span className="grow">
                  <span>{a.title}</span>
                  {a.assignee ? <span className="small muted minutes__who">{t("meeting.assignee", { name: nameOf(a.assignee) })}</span> : null}
                </span>
                {a.taskId ? (
                  <Link to={`/p/${pid}/tasks/${a.taskId}`} className="ui-btn ui-btn--secondary ui-btn--sm" data-testid="minutes-task-link">
                    <Icon name="board" size={16} />
                    {t("meeting.open_task")}
                  </Link>
                ) : (
                  <Button size="sm" icon="plus" loading={creating === i} onClick={() => void createTask(i)} data-testid="minutes-create-task">
                    {t("meeting.create_task")}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">{t("meeting.none")}</p>
        )}
      </section>
    </div>
  );
}

function MinutesEditor({ value, agents, onSave, onCancel }: { value: Minutes; agents: Agent[]; onSave(v: Minutes): void; onCancel(): void }) {
  const { t } = useT();
  const [summary, setSummary] = useState(value.summary);
  const [decisions, setDecisions] = useState(value.decisions.join("\n"));
  const [items, setItems] = useState(value.actionItems);
  const setItem = (i: number, patch: Partial<Minutes["actionItems"][number]>) => setItems((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const byId = useMemo(() => new Map(agents.map((a) => [a.id, a])), [agents]);
  return (
    <form
      className="stack minutes minutes--edit"
      data-testid="minutes-editor"
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ summary, decisions: decisions.split("\n"), actionItems: items });
      }}
    >
      <h2 className="section-title">{t("meeting.edit_minutes")}</h2>
      <Field label={t("meeting.summary")}>{(id) => <TextArea id={id} rows={4} value={summary} onChange={(e) => setSummary(e.target.value)} data-testid="minutes-summary-input" />}</Field>
      <Field label={t("meeting.decisions")} hint={t("meeting.one_per_line")}>
        {(id, d) => <TextArea id={id} aria-describedby={d} rows={4} value={decisions} onChange={(e) => setDecisions(e.target.value)} data-testid="minutes-decisions-input" />}
      </Field>
      <fieldset className="minutes__items">
        <legend className="ui-field__label">{t("meeting.action_items")}</legend>
        {items.map((a, i) => (
          <div key={i} className="minutes__item">
            <Input value={a.title} onChange={(e) => setItem(i, { title: e.target.value })} aria-label={t("meeting.action_title", { n: i + 1 })} data-testid="minutes-item-title" />
            <Select
              value={a.assignee ?? ""}
              onChange={(e) => setItem(i, { assignee: (e.target.value || null) as AgentId | null })}
              aria-label={t("meeting.action_assignee", { n: i + 1 })}
              disabled={!!a.taskId}
            >
              <option value="">{t("meeting.unassigned")}</option>
              {agents.map((ag) => (
                <option key={ag.id} value={ag.id}>
                  {ag.name}
                </option>
              ))}
              {a.assignee && !byId.has(a.assignee) ? <option value={a.assignee}>{a.assignee}</option> : null}
            </Select>
            <IconButton icon="close" label={t("meeting.remove_item", { n: i + 1 })} onClick={() => setItems((xs) => xs.filter((_, j) => j !== i))} />
          </div>
        ))}
        <Button size="sm" icon="plus" onClick={() => setItems((xs) => [...xs, { title: "", assignee: null, taskId: null }])} data-testid="minutes-add-item">
          {t("meeting.add_item")}
        </Button>
      </fieldset>
      <p className="small muted">{t("meeting.local_hint")}</p>
      <div className="row">
        <Button type="submit" variant="primary" icon="save" data-testid="minutes-save">
          {t("common.save")}
        </Button>
        <Button onClick={onCancel}>{t("common.cancel")}</Button>
      </div>
    </form>
  );
}
